import React from 'react';

import { BaseStatusTextProps } from '../../types/BaseStatusTextProps';
import { IconType } from '../../types/IconType';
import { Input } from '../../types/Input';
import { InputValue } from '../../types/InputValue';
import { Option } from '../../types/Option';
import { SharedChipProps } from '../../types/SharedChipProps';
import { StatusType } from '../../types/StatusType';
import { TextVariant } from '../../types/TextVariant';
import { typedMemo } from '../../utils/typedMemo';
import { Card, Props as CardProps } from '../Card/Card';
import { CollapsibleCard } from '../Card/subcomponents/CollapsibleCard';
import { Icon } from '../Icon/Icon';
import { Label } from '../Label/Label';
import { Layout } from '../Layout/Layout';
import { StatusText } from '../StatusText/StatusText';
import { Text } from '../Text/Text';

/** Additional UI configuration for a card option */
type CardOption<V> = Option<V> & {
  /** Configures the card option's banner message */
  banner?: {
    label: string;
    status: StatusType;
    text: string;
  };
  /** Visual mode for the card (bordered, embedded, raised, featured) */
  cardMode?: CardProps['mode'];
  /** Informative chips for the card header */
  chips?: SharedChipProps[];
  /** Position of chips relative to the title */
  chipsPosition?: 'left' | 'right';
  /** Collapsible state configuration */
  collapsible?: {
    /** The collapsed state of the card */
    isCollapsed: boolean;
    /** Callback to update collapse state */
    onToggle: (isCollapsed: boolean) => void;
  };
  /** Additional content to include with the card option */
  content?: React.ReactNode;
  /** Optional label text variant */
  labelVariant?: TextVariant;
  /** Configures the card option's status */
  status?: StatusType;
  /** Attach optional status text */
  statusText?: BaseStatusTextProps;
  /** Optional card width */
  width?: number | string;
  /** Optional direction for card content */
  contentDirection?: 'row' | 'column';
};

export interface Props<V, M extends boolean> extends Input<V, M> {
  /** Determines if single (radio) vs multi (checkbox) mode */
  isMulti: M;
  /** Select options */
  options: CardOption<V>[];
  /** Direction of card options */
  direction?: 'row' | 'column';
  /** Enables collapsible card mode */
  isCollapsible?: boolean;
  /** Mode to configure how the option is selected. Default is 'fill'. */
  mode?: 'bordered' | 'fill' | 'neutral';
  /** Spacing of card options */
  spacing?: 's' | 'xl';
}

export const CardSelect = typedMemo(
  <V extends unknown, M extends boolean>({
    direction = 'column',
    disabled,
    isCollapsible,
    isMulti,
    mode = 'fill',
    name,
    options,
    spacing = 's',
    value,
    onChange,
  }: Props<V, M>) => {
    type MultiValue = InputValue<V, true>;
    type SingleValue = InputValue<V, false>;

    const handleChange = (updatedValue: V) => {
      if (isMulti) {
        let updatedValues = (value as MultiValue) ?? [];
        if (updatedValues.includes(updatedValue)) {
          updatedValues = updatedValues.filter((v) => v !== updatedValue);
        } else {
          updatedValues = [...updatedValues, updatedValue];
        }
        onChange(updatedValues as InputValue<V, M>);
      } else {
        onChange(updatedValue as InputValue<V, M>);
      }
    };

    return (
      <Layout
        aria-label={name}
        direction={direction}
        disabled={disabled}
        preset={spacing}
        role="listbox"
      >
        {options.map(
          ({
            ariaLabel,
            banner,
            cardMode,
            chips,
            chipsPosition,
            collapsible,
            content,
            description,
            disabled: optionDisabled,
            label,
            labelVariant = 'body-bold',
            status,
            statusText,
            value: optionValue,
            width,
            contentDirection = 'column',
          }) => {
            const checked = isMulti
              ? Boolean((value as MultiValue)?.includes(optionValue))
              : (value as SingleValue) === optionValue;

            const cardContent = (
              <Layout
                direction="column"
                py={direction === 'row' ? 2 : undefined}
              >
                <Layout direction={contentDirection}>
                  <Layout direction="column" flex="auto" spacing={1}>
                    {!isCollapsible && (
                      <Layout align="center" spacing={1}>
                        <Icon icon={getSelectIcon(isMulti, checked)} />
                        <Layout
                          align="center"
                          justify="space-between"
                          spacing={2}
                          w="100%"
                        >
                          <Label variant={labelVariant}>{label}</Label>
                          {statusText && <StatusText {...statusText} />}
                        </Layout>
                      </Layout>
                    )}
                    <Text
                      preset="description"
                      textAlign="left"
                      pl={isCollapsible ? 0 : 6}
                    >
                      {description}
                    </Text>
                  </Layout>
                  <Layout flex="none">{content}</Layout>
                </Layout>
              </Layout>
            );

            const sharedCardProps = {
              banner,
              borderColor:
                mode === 'neutral'
                  ? undefined
                  : status && mode === 'bordered'
                    ? status
                    : mode === 'bordered'
                      ? checked
                        ? ('status.active' as const)
                        : undefined
                      : undefined,
              disabled: disabled || optionDisabled,
              domProps: {
                'aria-selected': checked,
              },
              flex: 'auto' as const,
              mode: cardMode ?? 'bordered',
              name: ariaLabel,
              role: 'option' as const,
              size: 's' as const,
              status:
                status && mode === 'fill'
                  ? status
                  : mode === 'fill'
                    ? checked
                      ? ('active' as const)
                      : undefined
                    : undefined,
              width,
            };

            if (isCollapsible && collapsible) {
              return (
                <CollapsibleCard
                  key={String(optionValue)}
                  {...sharedCardProps}
                  header={{
                    checkbox: {
                      checked,
                      onChange: () => handleChange(optionValue),
                    },
                    chips,
                    chipsPosition,
                    title: label,
                  }}
                  isCollapsed={collapsible.isCollapsed}
                  togglePosition="right"
                  onToggle={collapsible.onToggle}
                >
                  {cardContent}
                </CollapsibleCard>
              );
            }

            return (
              <Card
                key={String(optionValue)}
                {...sharedCardProps}
                onClick={() => handleChange(optionValue)}
              >
                {cardContent}
              </Card>
            );
          },
        )}
      </Layout>
    );
  },
);

const getSelectIcon = (isMulti: boolean, checked: boolean): IconType => {
  if (isMulti) {
    return checked ? 'checkbox-checked' : 'checkbox-empty';
  }
  return checked ? 'radio-checked' : 'radio-empty';
};
